/**
 * Unresolved Items Panel — Surface all risks, missing data, and assumptions transparently.
 */
import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { MaskedForecast } from "@/data/fie/types";
import { cn } from "@/lib/utils";

interface Props {
  forecast: MaskedForecast;
}

interface UnresolvedItem {
  source: string;
  category: string;
  text: string;
  severity: "warning" | "info" | "risk";
}

export function UnresolvedItemsPanel({ forecast }: Props) {
  const [expanded, setExpanded] = useState(false);

  // Collect all unresolved items from available data
  const items: UnresolvedItem[] = [];

  if (forecast.revenueLines) {
    for (const r of forecast.revenueLines) {
      if (r.lineage.unresolvedItems) {
        for (const u of r.lineage.unresolvedItems) {
          items.push({
            source: r.customerName,
            category: "Revenue",
            text: u,
            severity: u.includes("No signed") ? "risk" : u.includes("Permit") ? "warning" : "info",
          });
        }
      }
    }
  }

  if (forecast.costs) {
    for (const c of forecast.costs) {
      if (c.lineage.unresolvedItems) {
        for (const u of c.lineage.unresolvedItems) {
          items.push({
            source: c.category,
            category: "Cost",
            text: u,
            severity: u.includes("benchmark") ? "warning" : u.includes("risk") ? "info" : "warning",
          });
        }
      }
    }
  }

  if (items.length === 0) return null;

  const riskCount = items.filter(i => i.severity === "risk").length;
  const warningCount = items.filter(i => i.severity === "warning").length;

  return (
    <div className="rounded-lg border border-warning/20 bg-warning/5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <span className="text-sm font-medium text-warning">
            {items.length} Unresolved {items.length === 1 ? "Item" : "Items"}
          </span>
          {riskCount > 0 && (
            <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 text-[10px] h-5">
              {riskCount} risk
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-[10px] h-5">
              {warningCount} warning
            </Badge>
          )}
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-warning" /> : <ChevronRight className="h-4 w-4 text-warning" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-1 animate-in slide-in-from-top-1 duration-200">
          {items.map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-2 py-1.5 px-2 rounded text-xs hover:bg-warning/5 transition-colors"
            >
              <div className={cn(
                "h-2 w-2 rounded-full mt-1 shrink-0",
                item.severity === "risk" ? "bg-destructive/60" :
                item.severity === "warning" ? "bg-warning/60" : "bg-info/40"
              )} />
              <div className="flex-1 min-w-0">
                <span className="text-warning">{item.text}</span>
              </div>
              <Badge variant="outline" className="text-[9px] shrink-0 h-4 text-muted-foreground">
                {item.category}
              </Badge>
              <span className="text-[10px] text-muted-foreground/60 shrink-0 truncate max-w-[120px]">
                {item.source}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
